import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import usePermissions from '../../hooks/usePermissions';
import './ConnectedPlatforms.css';

/**
 * Sprint 4.5 Block 3 — Profile-flik som visar Player.voice_identities.
 *
 * Tre actions per platform:
 *   1. Anslut via challenge-kod (POST /api/voice/challenge/{platform})
 *      → visa kod + countdown, användaren skriver /verify <CODE> i platformens chat
 *      → bridge anropar verify-challenge, vi pollar identities tills verified syns
 *   2. Visa nuvarande verifierad koppling (om finns) + unlink-knapp
 *   3. För Discord visas legacy-koppling (Player.discord_user) som "Discord (legacy)"
 *      eftersom den fortfarande gäller via Player.discord_user FK utan separat
 *      VoiceIdentity-rad.
 */

const PLATFORMS = [
  { id: 'discord',   label: 'Discord',     icon: '💬', color: '#5865F2' },
  { id: 'fluxer',    label: 'Fluxer',      icon: '🌐', color: '#7c3aed' },
  { id: 'teamspeak', label: 'TeamSpeak',   icon: '🎧', color: '#2563eb' },
  { id: 'mumble',    label: 'Mumble',      icon: '🔊', color: '#f59e0b' },
  { id: 'matrix',    label: 'Matrix',      icon: '🔷', color: '#0dbd8b' },
];

/**
 * Profile-flik som visar Player.voice_identities och låter användaren
 * koppla nya platforms via challenge-kod.
 */
export default function ConnectedPlatforms({ getAuthHeaders, user }) {
  // Voice-plattformar är feature-flagged: admin kan dölja adaptrar som inte är
  // produktion-redo (t.ex. Fluxer pga platform-bugg som river WebRTC vid
  // bot-moves). Saknad flagga = visa (bakåtkompat).
  const { t } = useTranslation();
  const { features } = usePermissions();
  const enabledPlatforms = useMemo(
    () => PLATFORMS.filter((p) => {
      const key = `voice_platform_${p.id}_enabled`;
      return features?.[key] !== false;
    }),
    [features],
  );

  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeChallenge, setActiveChallenge] = useState(null);  // { platform, code, expiresAt }
  const [challengeBusy, setChallengeBusy] = useState(false);
  const [ts3ClaimOpen, setTs3ClaimOpen] = useState(false);
  const [fluxerClaimOpen, setFluxerClaimOpen] = useState(false);

  const fetchIdentities = useCallback(async () => {
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/voice/identities`, {
        headers: getAuthHeaders(),
      });
      if (!r.ok) {
        setError(t('connect.loadFailed'));
        return;
      }
      const data = await r.json();
      setIdentities(data.identities || []);
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => {
    fetchIdentities();
  }, [fetchIdentities]);

  // Pollra för verified-status medan challenge är aktiv
  useEffect(() => {
    if (!activeChallenge) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/voice/identities`, {
          headers: getAuthHeaders(),
        });
        if (!r.ok) return;
        const data = await r.json();
        setIdentities(data.identities || []);
        const verified = (data.identities || []).find(
          (vi) => vi.platform === activeChallenge.platform && vi.verified,
        );
        if (verified) {
          setActiveChallenge(null);
        }
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(id);
  }, [activeChallenge, getAuthHeaders]);

  const openConnect = (platform) => {
    if (platform === 'teamspeak') {
      setTs3ClaimOpen(true);
      return;
    }
    if (platform === 'fluxer') {
      setFluxerClaimOpen(true);
      return;
    }
    startChallenge(platform);
  };

  const startChallenge = async (platform) => {
    setChallengeBusy(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/voice/challenge/${platform}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.detail?.error || data.detail || t('connect.createCodeFailed'));
        return;
      }
      const data = await r.json();
      setActiveChallenge({
        platform,
        code: data.code,
        expiresAt: Date.now() + (data.expires_in || 300) * 1000,
        instruction: data.instruction,
      });
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setChallengeBusy(false);
    }
  };

  const unlink = async (identityId) => {
    if (!window.confirm(t('connect.confirmUnlink'))) return;
    try {
      const r = await fetch(`${API_URL}/api/voice/identities/${identityId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!r.ok) {
        setError(t('connect.unlinkFailed'));
        return;
      }
      fetchIdentities();
    } catch {
      setError(t('settings.networkError'));
    }
  };

  /**
   * Hitta status per platform:
   *   - 'verified-discord-legacy': användaren har Player.discord_user men ingen
   *     explicit VoiceIdentity-rad. Räknas som koppling (lookup-helpers
   *     faller tillbaka på discord_user).
   *   - 'verified': verifierad VoiceIdentity finns
   *   - 'pending': identity finns men inte verifierad ännu
   *   - 'unlinked': ingen koppling
   */
  const statusFor = (platform) => {
    const identity = identities.find((vi) => vi.platform === platform);
    if (identity) {
      return { state: identity.verified ? 'verified' : 'pending', identity };
    }
    if (platform === 'discord' && user?.discord_id) {
      return {
        state: 'verified-discord-legacy',
        identity: {
          platform: 'discord',
          platform_user_id: user.discord_id,
          nickname: user.discord_username,
          verified_via: 'legacy',
        },
      };
    }
    return { state: 'unlinked', identity: null };
  };

  return (
    <div className="connected-platforms">
      <h2 className="connected-platforms-title">{t('connect.title')}</h2>
      <p className="connected-platforms-help">
        {t('connect.platformsHelp')}
      </p>

      {error && <div className="connected-platforms-error">{error}</div>}

      {loading ? (
        <div className="connected-platforms-info">{t('live.loading')}</div>
      ) : (
        <div className="connected-platforms-list">
          {enabledPlatforms.map((p) => {
            const { state, identity } = statusFor(p.id);
            return (
              <PlatformRow
                key={p.id}
                platform={p}
                state={state}
                identity={identity}
                activeChallenge={activeChallenge?.platform === p.id ? activeChallenge : null}
                onConnect={() => openConnect(p.id)}
                onCancelChallenge={() => setActiveChallenge(null)}
                onUnlink={() => identity?.id && unlink(identity.id)}
                busy={challengeBusy}
              />
            );
          })}
        </div>
      )}

      {ts3ClaimOpen && (
        <TS3ClaimModal
          getAuthHeaders={getAuthHeaders}
          onClose={() => setTs3ClaimOpen(false)}
          onClaimed={() => {
            setTs3ClaimOpen(false);
            fetchIdentities();
          }}
        />
      )}
      {fluxerClaimOpen && (
        <FluxerClaimModal
          getAuthHeaders={getAuthHeaders}
          onClose={() => setFluxerClaimOpen(false)}
          onClaimed={() => {
            setFluxerClaimOpen(false);
            fetchIdentities();
          }}
        />
      )}
    </div>
  );
}

/**
 * Modal som listar TS3-klienter som är connected just nu och inte redan
 * claim:ade. Användaren klickar sin egen rad → POST claim → VoiceIdentity
 * skapas. Detta är primary path för TS3-koppling (challenge-kod-flowen
 * lever kvar som fallback men exponeras inte i UI för teamspeak).
 */
function TS3ClaimModal({ getAuthHeaders, onClose, onClaimed }) {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claimingCluid, setClaimingCluid] = useState('');

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Slashless: FastAPI-proxyn definierar rutten utan trailing slash; med
      // slash 307-redirectar den till intern host (127.0.0.1:8001) → nätverksfel.
      const r = await fetch(`${API_URL}/api/voice/ts3/available-clients`, {
        headers: getAuthHeaders(),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.error === 'ts3_unavailable') {
          setError(t('connect.ts3Unavailable'));
        } else {
          setError(data.error || t('connect.fetchListFailed'));
        }
        setClients([]);
        return;
      }
      setClients(data.clients || []);
    } catch {
      setError(t('settings.networkError'));
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const claim = async (cluid) => {
    setClaimingCluid(cluid);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/voice/ts3/claim`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ cluid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.error === 'cluid_taken') {
          setError(t('connect.ts3CluidTaken'));
        } else if (data.error === 'cluid_not_connected') {
          setError(t('connect.ts3NotConnected'));
        } else {
          setError(data.error || t('connect.claimFailed'));
        }
        return;
      }
      onClaimed();
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setClaimingCluid('');
    }
  };

  return (
    <div className="ts3-claim-overlay" onClick={onClose}>
      <div className="ts3-claim-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts3-claim-header">
          <h3>{t('connect.ts3ClaimTitle')}</h3>
          <button className="ts3-claim-close" onClick={onClose} aria-label={t('profile.close')}>×</button>
        </div>
        <p className="ts3-claim-help">
          {t('connect.ts3ClaimHelp')}
        </p>

        {error && <div className="ts3-claim-error">{error}</div>}

        {loading ? (
          <div className="ts3-claim-info">{t('connect.loadingList')}</div>
        ) : clients.length === 0 ? (
          <div className="ts3-claim-empty">
            <p>{t('connect.noFreeClients')}</p>
            <p className="ts3-claim-empty-hint">
              {t('connect.ts3EmptyHint')}
            </p>
          </div>
        ) : (
          <ul className="ts3-claim-list">
            {clients.map((c) => (
              <li key={c.cluid}>
                <button
                  className="ts3-claim-row"
                  onClick={() => claim(c.cluid)}
                  disabled={!!claimingCluid}
                  type="button"
                >
                  <span className="ts3-claim-nickname">{c.nickname}</span>
                  <span className="ts3-claim-cluid">{c.cluid.slice(0, 12)}…</span>
                  {claimingCluid === c.cluid && (
                    <span className="ts3-claim-busy">{t('connect.linking')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="ts3-claim-footer">
          <button
            className="platform-btn platform-btn-secondary"
            onClick={loadClients}
            disabled={loading || !!claimingCluid}
            type="button"
          >
            {t('connect.reloadList')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Fluxer-version av TS3ClaimModal — listar guild-medlemmar som inte är bots
 * och inte redan claim:ade. Klick på rad → POST claim → VoiceIdentity skapas
 * med verified_via='oauth' (Fluxer-medlemskapet är beviset).
 *
 * Inga snowflake-prefix visas eftersom Fluxer user-id:n är 17-19 siffror
 * och nicket är användarens egen identifierare.
 */
function FluxerClaimModal({ getAuthHeaders, onClose, onClaimed }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claimingId, setClaimingId] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Slashless (se ts3-kommentaren ovan) — annars 307 → intern host → nätverksfel.
      const r = await fetch(`${API_URL}/api/voice/fluxer/available-users`, {
        headers: getAuthHeaders(),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.error === 'fluxer_unavailable' || data.error === 'fluxer_unconfigured') {
          setError(t('connect.fluxerUnavailable'));
        } else {
          setError(data.error || t('connect.fetchListFailed'));
        }
        setUsers([]);
        return;
      }
      setUsers(data.users || []);
    } catch {
      setError(t('settings.networkError'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const claim = async (userId) => {
    setClaimingId(userId);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/voice/fluxer/claim`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.error === 'user_id_taken') {
          setError(t('connect.fluxerUserTaken'));
        } else if (data.error === 'user_not_in_voice') {
          setError(t('connect.fluxerNotInVoice'));
        } else {
          setError(data.error || t('connect.claimFailed'));
        }
        return;
      }
      onClaimed();
    } catch {
      setError(t('settings.networkError'));
    } finally {
      setClaimingId('');
    }
  };

  return (
    <div className="ts3-claim-overlay" onClick={onClose}>
      <div className="ts3-claim-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts3-claim-header">
          <h3>{t('connect.fluxerClaimTitle')}</h3>
          <button className="ts3-claim-close" onClick={onClose} aria-label={t('profile.close')}>×</button>
        </div>
        <p className="ts3-claim-help">
          {t('connect.fluxerClaimHelp')}
        </p>

        {error && <div className="ts3-claim-error">{error}</div>}

        {loading ? (
          <div className="ts3-claim-info">{t('connect.loadingList')}</div>
        ) : users.length === 0 ? (
          <div className="ts3-claim-empty">
            <p>{t('connect.noFreeUsers')}</p>
            <p className="ts3-claim-empty-hint">
              {t('connect.fluxerEmptyHint')}
            </p>
          </div>
        ) : (
          <ul className="ts3-claim-list">
            {users.map((u) => (
              <li key={u.user_id}>
                <button
                  className="ts3-claim-row"
                  onClick={() => claim(u.user_id)}
                  disabled={!!claimingId}
                  type="button"
                >
                  <span className="ts3-claim-nickname">{u.nickname}</span>
                  <span className="ts3-claim-cluid">{u.user_id}</span>
                  {claimingId === u.user_id && (
                    <span className="ts3-claim-busy">{t('connect.linking')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="ts3-claim-footer">
          <button
            className="platform-btn platform-btn-secondary"
            onClick={loadUsers}
            disabled={loading || !!claimingId}
            type="button"
          >
            {t('connect.reloadList')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * En rad per platform. Visar nuvarande status och åtgärdsknappar.
 */
function PlatformRow({ platform, state, identity, activeChallenge, onConnect, onCancelChallenge, onUnlink, busy }) {
  const { t } = useTranslation();
  const isActive = activeChallenge !== null;
  return (
    <div className={`platform-row platform-row-${state}`}>
      <div className="platform-row-main">
        <div className="platform-icon" style={{ background: platform.color }}>
          <span aria-hidden>{platform.icon}</span>
        </div>
        <div className="platform-info">
          <div className="platform-name">{platform.label}</div>
          <PlatformStatus state={state} identity={identity} />
        </div>
      </div>

      {isActive ? (
        <ChallengeBox
          challenge={activeChallenge}
          platformLabel={platform.label}
          onCancel={onCancelChallenge}
        />
      ) : (
        <div className="platform-actions">
          {state === 'verified' && (
            <button className="platform-btn platform-btn-secondary" onClick={onUnlink}>
              {t('connect.unlink')}
            </button>
          )}
          {state === 'verified-discord-legacy' && (
            <span className="platform-legacy-note">{t('connect.autoViaDiscord')}</span>
          )}
          {(state === 'unlinked' || state === 'pending') && (
            <button
              className="platform-btn"
              onClick={onConnect}
              disabled={busy}
              type="button"
            >
              {state === 'pending' ? t('connect.retry') : t('connect.connect')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Status-rad per platform — färgad ikon + nickname + verifieringsmetod.
 */
function PlatformStatus({ state, identity }) {
  const { t } = useTranslation();
  switch (state) {
    case 'verified':
      return (
        <div className="platform-status platform-status-verified">
          ✓ {identity.nickname || identity.platform_user_id}
          <span className="platform-status-meta">
            {' '}· {t('connect.verifiedVia')} {identity.verified_via}
          </span>
        </div>
      );
    case 'verified-discord-legacy':
      return (
        <div className="platform-status platform-status-verified">
          ✓ {identity.nickname || identity.platform_user_id}
          <span className="platform-status-meta"> · {t('connect.legacyLink')}</span>
        </div>
      );
    case 'pending':
      return (
        <div className="platform-status platform-status-pending">
          ⏱ {t('connect.pendingVerify')}
        </div>
      );
    case 'unlinked':
    default:
      return (
        <div className="platform-status platform-status-unlinked">
          {t('connect.notLinked')}
        </div>
      );
  }
}

/**
 * Visar en aktiv challenge-kod med countdown. Användaren skriver
 * `/verify <CODE>` i platformens chat för att slutföra kopplingen.
 */
function ChallengeBox({ challenge, platformLabel, onCancel }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(challenge.expiresAt - Date.now());
  useEffect(() => {
    const iv = setInterval(() => setRemaining(challenge.expiresAt - Date.now()), 1000);
    return () => clearInterval(iv);
  }, [challenge.expiresAt]);

  const mins = Math.max(0, Math.floor(remaining / 60000));
  const secs = Math.max(0, Math.floor((remaining % 60000) / 1000));
  const expired = remaining <= 0;

  return (
    <div className="platform-challenge">
      <div className="platform-challenge-code">
        <code>/verify {challenge.code}</code>
      </div>
      <div className="platform-challenge-info">
        {expired ? (
          <span className="platform-challenge-expired">{t('connect.codeExpired')}</span>
        ) : (
          t('connect.typeWithin', { platform: platformLabel, time: `${mins}:${String(secs).padStart(2, '0')}` })
        )}
      </div>
      <button className="platform-btn platform-btn-secondary" onClick={onCancel}>
        {t('matchDetail.cancel')}
      </button>
    </div>
  );
}
